import { loadCSS, loadScript } from './utils';

// Enrich the window object with analytics services.
declare global {
  interface Window extends Record<string, unknown> {
    dataLayer: {
      push: (event: Record<string, unknown>) => void;
    };
    arduinoCookieSolution: {
      initCookieConsent: (isAdult: boolean) => void;
    };
    _iub: {
      cs: {
        consent: {
          purposes: { [key: string]: boolean };
        };
      };
    };
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    analytics: any;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    profitwell: (event: string, data: any) => void;
  }
}

/*
 * =============================================================================
 * Analytics Generators
 * =============================================================================
 */

/**
 * Initialize Google Analytics.
 */
async function initGA(env: { GTM_ID: string }) {
  await loadScript(`https://www.googletagmanager.com/gtm.js?id=${process.env.GATSBY_GTM_ID}`, 'gtm-script');

  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({ 'gtm.start': new Date().getTime(), event: 'gtm.js' });

  // Push page metadata to the data layer
  window.dataLayer.push({
    event: 'pageMetaData',
    page: {
      type: 'home',
      title: 'Arduino Cloud',
      path: window.location.pathname,
      environment: '%VITE_HF_CDN_ENV%',
      region: 'global',
      brand: 'arduino',
      language: 'EN',
    },
  });
}

/**
 * Initialize Segment analytics.
 */
async function initSegment(env: { TOKEN: string; SCRIPT: string }) {
  const analytics = window.analytics || [];
  window.analytics = analytics;
  if (analytics.initialize) return;

  if (analytics.invoked) {
    // eslint-disable-next-line no-console
    console.error('Segment snippet included twice.');
    return;
  }

  analytics.invoked = !0;
  analytics.methods = [
    'trackSubmit',
    'trackClick',
    'trackLink',
    'trackForm',
    'pageview',
    'identify',
    'reset',
    'group',
    'track',
    'ready',
    'alias',
    'debug',
    'page',
    'screen',
    'once',
    'off',
    'on',
    'addSourceMiddleware',
    'addIntegrationMiddleware',
    'setAnonymousId',
    'addDestinationMiddleware',
    'register',
  ];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  analytics.factory = (e: any) => () => {
    // eslint-disable-next-line prefer-rest-params
    const i = Array.prototype.slice.call(arguments);
    if (['track', 'screen', 'alias', 'group', 'page', 'identify'].indexOf(e) > -1) {
      const c = document.querySelector("link[rel='canonical']");
      i.push({
        __t: 'bpc',
        c: (c && c.getAttribute('href')) || undefined,
        p: window.location.pathname,
        u: window.location.href,
        s: window.location.search,
        t: document.title,
        r: document.referrer,
      });
    }
    i.unshift(e);
    analytics.push(i);
    return analytics;
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  analytics.methods.forEach((i: any) => {
    const key = analytics.methods[i];
    analytics[key] = analytics.factory(key);
  });

  analytics._writeKey = env.TOKEN;
  analytics._cdn = 'https://evs.aayinltcs.arduino.cc';
  analytics.SNIPPET_VERSION = '5.2.0';

  analytics.load = async (e: string) => {
    await loadScript(env.SCRIPT, 'segment-script');
    analytics._loadOptions = e;
  };

  await analytics.load(analytics._writeKey);
}

/**
 * Initialize Iubenda Cookie Solution.
 */
async function initCookieSolution(env: { HF_CDN_URL: string }) {
  loadCSS(`${env.HF_CDN_URL}/cookieSolution.css`);
  await loadScript(`${env.HF_CDN_URL}/cookieSolution.js`, 'cookieSolution');

  // Initialize Iubenda
  window.arduinoCookieSolution.initCookieConsent(false);
}

/*
 * =============================================================================
 * Analytics Bootstrap
 * =============================================================================
 */

/**
 * The `analyticsBootstrap` function initializes various analytics tools and triggers page tracking.
 * - Loads Iubenda Cookie Solution
 * - Loads Google Analytics (and fires some events)
 * - Loads Segment
 * - Evaluates Iubenda consent and triggers page tracking
 * - Triggers identify event for Profitwell (loaded via Segment)
 */
async function analyticsBootstrap(
  profile: any,
  env: {
    /**
     * Iubenda configuration
     * Automatically handles the Iubenda Cookie Solution via header-footer
     */
    iubenda: Parameters<typeof initCookieSolution>[0];
    /**
     * Google Analytics configuration
     * Automatically loads the GTM script and pushes some init events.
     */
    ga: Parameters<typeof initGA>[0];
    /**
     * Segment configuration.
     * Loads the segment script and initializes the analytics object.
     */
    segment?: Parameters<typeof initSegment>[0];
    /**
     * Profitwell (via Segment, should only be true on production)
     */
    profitwell?: boolean;
  }
) {
  const IUBENDA_MONITORING_INDEX = 4;

  async function exist(predicate: () => boolean, retry = 0, delay = 1000): Promise<void> {
    return new Promise<void>((res, rej) => {
      if (retry > 10) rej();
      if (predicate()) res();
      else setTimeout(() => exist(predicate, retry + 1).then(res), delay);
    });
  }

  await initCookieSolution(env.iubenda);
  await initGA(env.ga);

  if (env.segment) await initSegment(env.segment);

  // Start Segment
  exist(
    () =>
      !!window.analytics?.page && !!window._iub && window._iub?.cs?.consent?.purposes && window._iub?.cs?.consent?.purposes[IUBENDA_MONITORING_INDEX]
  )
    .then(() => window.analytics.page())
    // eslint-disable-next-line no-console
    .catch(() => console.warn('Page not triggered!'));

  // Start Profitwell (via Segment)
  if (env.profitwell) {
    exist(() => !!window.analytics?.identify && !!window._iub)
      .then(() => {
        const statisticCookie = window._iub?.cs?.consent?.purposes[IUBENDA_MONITORING_INDEX];
        window.analytics.identify(
          profile['http://arduino.cc/id'],
          { email: profile.email },
          { integrations: { All: false, 'Segment.io': statisticCookie, ProfitWell: statisticCookie } }
        );
      })
      // eslint-disable-next-line no-console
      .catch(() => console.warn('Profitwell not triggered!'));
  }
}

export { analyticsBootstrap };
